import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../utils/auth';
import { refundRequests as refundRequestsApi } from '../../utils/api';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { CheckCircle2, XCircle, Clock } from 'lucide-react';
import toast from 'react-hot-toast';

const STATUS_CONFIG = {
  pending:  { label: 'En attente', color: 'var(--amber)',  bg: 'rgba(245,166,35,0.1)',  icon: <Clock size={13}/> },
  approved: { label: 'Approuvé',   color: 'var(--teal)',   bg: 'rgba(0,210,180,0.1)',   icon: <CheckCircle2 size={13}/> },
  rejected: { label: 'Rejeté',     color: 'var(--red)',    bg: 'rgba(255,77,109,0.1)',  icon: <XCircle size={13}/> },
};

function ReviewModal({ request, action, onClose, onDone }) {
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      if (action === 'approve') await refundRequestsApi.approve(request.id, { notes });
      else                      await refundRequestsApi.reject(request.id,  { notes });
      toast.success(action === 'approve' ? 'Remboursement approuvé et enregistré' : 'Demande rejetée');
      onDone();
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 420 }}>
        <h3 className="modal-title">
          {action === 'approve' ? '✓ Approuver le remboursement' : '✗ Rejeter la demande'}
        </h3>
        <div style={{ background: 'var(--bg-elevated)', borderRadius: 10, padding: '12px 14px', marginBottom: 16, fontSize: 13 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>{request.booking_ref} — {request.room_name}</div>
          <div style={{ color: 'var(--text-2)' }}>{request.customer_name}</div>
          <div style={{ marginTop: 8, fontWeight: 700, fontSize: 16, color: action === 'approve' ? 'var(--teal)' : 'var(--red)' }}>
            {parseFloat(request.amount).toFixed(2)} €
          </div>
        </div>

        {action === 'approve' && (
          <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 14,
            background: 'rgba(0,210,180,0.06)', border: '1px solid var(--teal)', fontSize: 12, color: 'var(--text-2)' }}>
            Un paiement négatif de <strong>{parseFloat(request.amount).toFixed(2)} €</strong> sera enregistré sur la réservation.
          </div>
        )}

        <div className="form-group">
          <label className="form-label">Note (optionnelle)</label>
          <textarea className="input" rows={3} style={{ resize: 'vertical' }}
            value={notes} onChange={e => setNotes(e.target.value)}
            placeholder={action === 'approve' ? 'Ex: Remboursé par virement le 12/05…' : 'Raison du rejet…'}/>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button className={`btn ${action === 'approve' ? 'btn-teal' : 'btn-danger'}`}
            onClick={submit} disabled={saving}>
            {saving ? <span className="spinner" style={{ width: 16, height: 16 }}/> :
              action === 'approve' ? 'Confirmer le remboursement' : 'Rejeter la demande'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminRefunds() {
  const { can } = useAuth();
  if (!can('payments.manage')) return (
    <div className="page-body"><div className="empty-state card"><p>Accès non autorisé</p></div></div>
  );

  const [requests, setRequests] = useState([]);
  const [filter, setFilter]     = useState('pending');
  const [modal, setModal]       = useState(null); // { request, action }
  const [loading, setLoading]   = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await refundRequestsApi.list(filter || undefined);
      setRequests(r.data.refundRequests);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  return (
    <>
      <div className="page-header">
        <div>
          <h2>Demandes de remboursement</h2>
          <p>Validez ou rejetez les remboursements suite aux annulations de réservations</p>
        </div>
        {pendingCount > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px',
            background: 'rgba(245,166,35,0.1)', border: '1px solid var(--amber)',
            borderRadius: 10, fontSize: 13, color: 'var(--amber)', fontWeight: 600 }}>
            <Clock size={14}/> {pendingCount} en attente
          </div>
        )}
      </div>

      <div className="page-body">
        {/* Filtres */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          {[
            ['pending',  'En attente'],
            ['approved', 'Approuvées'],
            ['rejected', 'Rejetées'],
            ['',         'Toutes'],
          ].map(([val, label]) => (
            <button key={val} onClick={() => setFilter(val)}
              className={`btn btn-sm ${filter === val ? 'btn-primary' : 'btn-secondary'}`}>
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="empty-state"><span className="spinner"/></div>
        ) : requests.length === 0 ? (
          <div className="empty-state card">
            <CheckCircle2 size={32} style={{ opacity: 0.3 }}/>
            <p>Aucune demande {filter === 'pending' ? 'en attente' : ''}</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Réservation</th>
                  <th>Client</th>
                  <th>Session</th>
                  <th className="text-right">Montant</th>
                  <th>Raison</th>
                  <th>Demandé par</th>
                  <th>Statut</th>
                  {filter === 'pending' && can('payments.manage') && <th/>}
                </tr>
              </thead>
              <tbody>
                {requests.map(rr => {
                  const conf = STATUS_CONFIG[rr.status];
                  return (
                    <tr key={rr.id}>
                      <td>
                        <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13 }}>
                          {rr.booking_ref}
                        </span>
                        <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{rr.room_name}</div>
                      </td>
                      <td style={{ fontWeight: 500 }}>{rr.customer_name}</td>
                      <td style={{ fontSize: 13, color: 'var(--text-2)' }}>
                        {rr.session_date
                          ? format(new Date(rr.session_date + 'T12:00'), 'd MMM yyyy', { locale: fr })
                          : '—'}
                      </td>
                      <td className="text-right">
                        <span style={{ fontWeight: 700, color: 'var(--amber)', fontSize: 15 }}>
                          {parseFloat(rr.amount).toFixed(2)} €
                        </span>
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--text-2)', maxWidth: 200 }}>
                        {rr.reason || '—'}
                      </td>
                      <td style={{ fontSize: 12 }}>
                        {rr.requested_by_first
                          ? `${rr.requested_by_first} ${rr.requested_by_last}`
                          : '—'}
                        <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
                          {rr.requested_at
                            ? format(new Date(rr.requested_at), 'dd/MM/yyyy HH:mm')
                            : ''}
                        </div>
                      </td>
                      <td>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          padding: '4px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600,
                          background: conf.bg, color: conf.color,
                        }}>
                          {conf.icon} {conf.label}
                        </span>
                        {rr.notes && (
                          <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 3 }}>
                            {rr.notes}
                          </div>
                        )}
                      </td>
                      {rr.status === 'pending' && can('payments.manage') && (
                        <td>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button className="btn btn-teal btn-sm"
                              onClick={() => setModal({ request: rr, action: 'approve' })}>
                              <CheckCircle2 size={12}/> Approuver
                            </button>
                            <button className="btn btn-danger btn-sm"
                              onClick={() => setModal({ request: rr, action: 'reject' })}>
                              <XCircle size={12}/> Rejeter
                            </button>
                          </div>
                        </td>
                      )}
                      {rr.status !== 'pending' && filter === 'pending' && <td/>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <ReviewModal
          request={modal.request}
          action={modal.action}
          onClose={() => setModal(null)}
          onDone={() => { setModal(null); load(); }}
        />
      )}
    </>
  );
}
